<template>
  <div
    ref="rootEl"
    class="process-trace"
    :class="{ 'process-trace--wide': isWide, 'process-trace--live': isLive }"
  >
    <!-- 工具栏：浅紫底 + 32px 高，运行中图标原色、结束后降不透明度（对齐目标 TurnCard） -->
    <div class="process-trace__head" @click="toggleExpanded">
      <img
        class="process-trace__start"
        :class="{ 'is-working': working }"
        :src="require('@/assets/imgs/ai/tool1.png')"
        alt=""
        draggable="false"
      />
      <span class="process-trace__title">{{ headerLabel }}</span>
      <span class="process-trace__count">{{ stepCount }}</span>
      <span class="process-trace__chevron" :class="{ 'is-expanded': expanded }">
        <i class="el-icon-arrow-right"></i>
      </span>
      <el-dropdown
        trigger="click"
        placement="bottom-end"
        @click.native.stop
        @command="onMoreCommand"
      >
        <button class="process-trace__more" type="button" title="更多" @click.stop>
          <i class="el-icon-more"></i>
        </button>
        <el-dropdown-menu slot="dropdown">
          <el-dropdown-item command="expandAll">全部展开</el-dropdown-item>
          <el-dropdown-item command="collapseAll">全部收起</el-dropdown-item>
        </el-dropdown-menu>
      </el-dropdown>
    </div>

    <!-- 展开列表：浅蓝底，限高自身滚动 -->
    <transition name="trace-slide">
      <div
        v-if="expanded"
        ref="bodyEl"
        class="process-trace__body"
      >
        <template v-for="(row, index) in rows">
          <div
            :key="row.key"
            class="trace-row"
            :class="{
              'trace-row--running': isRowRunning(row),
              'trace-row--pending': isRowPending(row)
            }"
            :style="rowStyle(row, index)"
            @click="onRowClick(row)"
          >
            <!-- 行首图标（对齐目标端 TurnCard ActivityStatusIcon）：
                 工具行：运行中=工具调用.png、已完成=步骤完成.png、待开始=实心圆、出错=虚线圆；
                 思考 / 过程叙述行：保持虚线圆 -->
            <span class="trace-row__icon">
              <template v-if="row.kind === 'tool'">
                <img
                  v-if="row.status === 'running'"
                  :src="require('@/assets/imgs/ai/tool2.png')"
                  class="trace-icon-img"
                  alt=""
                />
                <!-- 已完成且带品牌图标（Python / git / chrome …）时优先显示，对齐目标端 customIcon -->
                <img
                  v-else-if="row.status === 'completed' && row.brand"
                  :src="row.brand.icon"
                  class="trace-icon-img trace-icon-img--brand"
                  alt=""
                />
                <img
                  v-else-if="row.status === 'completed'"
                  :src="require('@/assets/imgs/ai/toolDone.png')"
                  class="trace-icon-img"
                  alt=""
                />
                <svg
                  v-else-if="row.status === 'pending'"
                  class="trace-icon-svg trace-icon-svg--pending"
                  xmlns="http://www.w3.org/2000/svg"
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <circle cx="12" cy="12" r="10"></circle>
                </svg>
                <svg
                  v-else
                  class="trace-icon-svg"
                  xmlns="http://www.w3.org/2000/svg"
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M10.1 2.182a10 10 0 0 1 3.8 0"></path>
                  <path d="M13.9 21.818a10 10 0 0 1-3.8 0"></path>
                  <path d="M17.609 3.72a10 10 0 0 1 2.69 2.7"></path>
                  <path d="M2.182 13.9a10 10 0 0 1 0-3.8"></path>
                  <path d="M20.28 17.61a10 10 0 0 1-2.7 2.69"></path>
                  <path d="M21.818 10.1a10 10 0 0 1 0 3.8"></path>
                  <path d="M3.721 6.391a10 10 0 0 1 2.7-2.69"></path>
                  <path d="m6.163 21.117-2.906.85a1 1 0 0 1-1.236-1.169l.965-2.98"></path>
                </svg>
              </template>
              <svg
                v-else-if="row.icon === 'ℹ️'"
                class="trace-icon-svg"
                :class="{ 'is-spinning': row.status === 'running' }"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M10.1 2.182a10 10 0 0 1 3.8 0"></path>
                <path d="M13.9 21.818a10 10 0 0 1-3.8 0"></path>
                <path d="M17.609 3.72a10 10 0 0 1 2.69 2.7"></path>
                <path d="M2.182 13.9a10 10 0 0 1 0-3.8"></path>
                <path d="M20.28 17.61a10 10 0 0 1-2.7 2.69"></path>
                <path d="M21.818 10.1a10 10 0 0 1 0 3.8"></path>
                <path d="M3.721 6.391a10 10 0 0 1 2.7-2.69"></path>
                <path d="m6.163 21.117-2.906.85a1 1 0 0 1-1.236-1.169l.965-2.98"></path>
              </svg>
              <template v-else>{{ row.icon }}</template>
            </span>

            <!-- 工具行：隐藏工具名，只留 diff 徽标 + 文件徽标 + 意图 + ·参数摘要 + 悬停箭头 -->
            <template v-if="row.kind === 'tool'">
              <span v-if="row.diffStats" class="trace-row__diff">
                <span v-if="row.diffStats.deletions > 0" class="trace-row__del">
                  {{ row.diffStats.deletions }}
                </span>
                <span v-if="row.diffStats.additions > 0" class="trace-row__add">
                  {{ row.diffStats.additions }}
                </span>
              </span>
              <span v-if="row.fileBadge" class="trace-row__file">{{ row.fileBadge }}</span>
              <span class="trace-row__compound">
                <span v-if="row.brand && row.brand.label" class="trace-row__brand">
                  {{ row.brand.label }}
                </span>
                <template v-if="row.intent">
                  <span>{{ row.intent }}</span>
                </template>
                <template v-if="row.paramsSummary">
                  <span class="trace-row__sep"> · </span>
                  <span class="trace-row__params">{{ row.paramsSummary }}</span>
                </template>
              </span>
              <span
                v-if="row.msg"
                class="trace-row__arrow"
                title="查看详情"
                @click.stop="$emit('open-detail', row.msg)"
              >
                <i class="el-icon-top-right"></i>
              </span>
            </template>

            <!-- 思考 / 过程叙述行：点击整行展开全文；右侧箭头悬停才出现，点击打开全屏浮层 -->
            <template v-else>
              <span class="trace-row__text" :title="row.text">{{ row.text }}</span>
              <span v-if="isRowPending(row)" class="trace-row__caret"></span>
              <span class="trace-row__note-chevron" :class="{ 'is-open': isRowOpen(row) }">
                <i class="el-icon-arrow-right"></i>
              </span>
              <span
                v-if="row.text"
                class="trace-row__arrow"
                title="全屏查看"
                @click.stop="$emit('open-text', { text: row.text })"
              >
                <i class="el-icon-top-right"></i>
              </span>
            </template>
          </div>

          <div
            v-if="row.kind !== 'tool' && isRowOpen(row)"
            :key="row.key + '-text'"
            class="trace-note-detail"
          >{{ row.text }}</div>
        </template>

        <!-- TodoWrite 列表：只有真的带 todos 时才渲染 -->
        <div v-if="todos.length" class="process-trace__todos">
          <div v-for="todo in todos" :key="todo.key" class="todo-row" :class="'is-' + todo.status">
            <span class="todo-row__mark">
              <template v-if="todo.status === 'completed'">✓</template>
              <template v-else-if="todo.status === 'in_progress'">◐</template>
              <template v-else>○</template>
            </span>
            <span class="todo-row__text">{{ todo.content }}</span>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import {
  buildTraceRows,
  traceHeaderLabel,
  traceStepCount,
  extractTodos
} from '../utils/processTrace'

/** 逐行渐入的错峰间隔与只作用于前 N 行 */
const STAGGER_MS = 30
const STAGGER_LIMIT = 10

export default {
  name: 'CosmoClawProcessTrace',
  props: {
    // 属于同一条 trace 的过程消息（思考 / 工具调用 / 过程叙述），按原始顺序
    msgs: { type: Array, default: () => [] },
    // 这一轮是否还在进行（决定默认展开、行状态、以及自动贴底）
    isLive: { type: Boolean, default: false },
    // 正文气泡是否已开始输出：一开始就结束"执行中"，头部图标停止旋转
    answerStarted: { type: Boolean, default: false },
    // 是否是消息流里最后一个 trace（只有它算"当前这一轮"）
    isLast: { type: Boolean, default: false },
    isWide: { type: Boolean, default: false }
  },
  data() {
    return {
      expanded: this.isLast && this.isLive,
      // 行内已展开全文的叙述行 key 集合
      openRowKeys: [],
      // 失去 isLast 身份后登记"待收起"，由父组件巡检、待完全滚出可视区再收
      pendingCollapse: false,
      // 用户手动展开过（表头/收起按钮点击），其意愿优先于自动收起
      userExpanded: false,
      // 展开瞬间给前 10 行做错峰渐入；700ms 后关闭，避免流式新增行也排队等延迟
      animateRows: false,
      rowAnimTimer: null
    }
  },
  computed: {
    rows() {
      return buildTraceRows(this.msgs, this.isLive)
    },
    headerLabel() {
      return traceHeaderLabel(this.rows, this.isLive, this.answerStarted)
    },
    stepCount() {
      return traceStepCount(this.rows)
    },
    todos() {
      return extractTodos(this.msgs)
    },
    /** 是否处于"执行中"（本轮还在跑、且正文尚未开始输出）→ 头部图标高亮并旋转 */
    working() {
      return this.isLive && !this.answerStarted
    }
  },
  watch: {
    rows() {
      this.$nextTick(() => this.scrollBodyToBottom())
    },
    // 收起/展开按"当前状态"同步，而不是按"边沿"触发（原实现会在 isLast/isLive 同帧变化时收不起来）
    isLast() {
      this.syncExpanded()
    },
    isLive(val) {
      if (val) this.userExpanded = false
      this.syncExpanded()
    },
    expanded(val) {
      clearTimeout(this.rowAnimTimer)
      if (!val) {
        this.animateRows = false
        return
      }
      this.animateRows = true
      this.rowAnimTimer = setTimeout(() => {
        this.animateRows = false
      }, 700)
    }
  },
  mounted() {
    if (this.expanded) {
      this.animateRows = true
      this.rowAnimTimer = setTimeout(() => {
        this.animateRows = false
      }, 700)
      this.$nextTick(() => this.scrollBodyToBottom())
    }
  },
  beforeDestroy() {
    clearTimeout(this.rowAnimTimer)
  },
  methods: {
    toggleExpanded() {
      this.pendingCollapse = false
      this.expanded = !this.expanded
      this.userExpanded = this.expanded
    },
    /**
     * 按当前状态决定展开/收起（幂等）：
     *   1. 本轮进行中 → 保持展开；
     *   2. 本轮结束且仍是最后一块 → 当场收起（用户手动展开过的除外）；
     *   3. 已不是最后一块 → 登记待收起，等完全滚出可视区后静默收起。
     */
    syncExpanded() {
      if (this.isLive) {
        this.pendingCollapse = false
        return
      }
      if (this.isLast) {
        if (!this.userExpanded) this.collapseAfterRun()
        return
      }
      if (this.expanded && !this.userExpanded) this.pendingCollapse = true
    },
    isRowOpen(row) {
      return this.openRowKeys.indexOf(row.key) !== -1
    },
    /** 工具行是否还在执行（决定行首图标轻微脉动）。 */
    isRowRunning(row) {
      return !!(row.kind === 'tool' && row.msg && row.msg.phase !== 'result')
    },
    /** 这行是不是"正在打字的过程叙述"（流式中整段铺开 + 灰色光标）。 */
    isRowPending(row) {
      return !!(
        this.isLive &&
        row.kind !== 'tool' &&
        row.msg &&
        row.msg.isStreaming
      )
    },
    /** 行内联样式：子代理缩进（有 depth 才缩进）+ 展开错峰延迟。 */
    rowStyle(row, index) {
      const style = {}
      if (row.depth > 0) style.paddingLeft = row.depth * 16 + 'px'
      if (this.animateRows && index < STAGGER_LIMIT) {
        style.animationDelay = index * STAGGER_MS + 'ms'
      }
      return style
    },
    /** 工具行 → 打开详情浮层（MEDIA 行改为打开媒体）；叙述行 → 行内展开全文。 */
    onRowClick(row) {
      if (row.kind === 'tool') {
        if (row.openKind === 'link') {
          this.$emit('open-media', row)
          return
        }
        if (row.msg) this.$emit('open-detail', row.msg)
        return
      }
      const key = row.key
      this.openRowKeys =
        this.openRowKeys.indexOf(key) === -1
          ? this.openRowKeys.concat(key)
          : this.openRowKeys.filter((k) => k !== key)
    },
    onMoreCommand(command) {
      if (command === 'expandAll') this.expandAllRows()
      else if (command === 'collapseAll') this.collapseAllRows()
      this.$emit('more', command)
    },
    expandAllRows() {
      this.openRowKeys = this.rows
        .filter((row) => row.kind !== 'tool' && row.text)
        .map((row) => row.key)
    },
    collapseAllRows() {
      this.openRowKeys = []
    },
    scrollBodyToBottom() {
      const el = this.$refs.bodyEl
      if (!el || !this.isLive) return
      el.scrollTop = el.scrollHeight
    },
    collapseWithoutAnim() {
      if (!this.expanded) return
      this.expanded = false
    },
    collapseAfterRun() {
      if (!this.expanded) return
      this.pendingCollapse = false
      this.expanded = false
    },
    /**
     * 实时测量：整条 trace 是否已完全滚出聊天可视区。
     * 取不到元素/测量能力时一律视为"还没滚出"，宁可不收也不当面误收。
     */
    isFullyScrolledOut() {
      const el = this.$refs.rootEl
      if (!el || typeof el.getBoundingClientRect !== 'function') return false
      const rect = el.getBoundingClientRect()
      if (rect.height <= 0) return false
      let viewTop = 0
      let viewBottom = window.innerHeight || document.documentElement.clientHeight || 0
      // 必须量测真正的滚动视口 .chat-scroll-wrapper（overflow-y:auto）
      const scroller = el.closest && el.closest('.chat-scroll-wrapper')
      if (scroller && typeof scroller.getBoundingClientRect === 'function') {
        const sr = scroller.getBoundingClientRect()
        viewTop = sr.top
        viewBottom = sr.bottom
      }
      return rect.bottom <= viewTop || rect.top >= viewBottom
    },
    /** 供父组件巡检调用：仅"待收起且已完全滚出可视区"时才收起。 */
    collapseIfScrolledOut() {
      if (!this.pendingCollapse || !this.expanded) return false
      if (this.isFullyScrolledOut()) {
        this.pendingCollapse = false
        this.collapseWithoutAnim()
        return true
      }
      return false
    }
  }
}
</script>

<style lang="scss" scoped>
/* ===== 宽屏形态：抽屉放大态 / 数字人会话页等宽容器下，半宽靠左 ===== */
.process-trace--wide {
  margin-right: auto;
}

.process-trace {
  --mc-border: #e8ecf1;
  /* 对齐目标 text-muted-foreground：foreground 50% 混白 */
  --mc-text-tertiary: #333;
  margin-bottom: 12px;
  border: 1px solid var(--mc-border);
  border-radius: 8px;
  background: #fbfcfe;
  overflow: hidden;
}

/* ===== 工具栏（对齐目标：32px 高、浅紫底、左 22px） ===== */
.process-trace__head {
  display: flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 12px 0 12px;
  background: #e7edfe;
  cursor: pointer;
  user-select: none;
}

/* 启动图标：静态，仅用不透明度区分运行中/已结束 */
.process-trace__start {
  width: 14px;
  height: 14px;
  object-fit: contain;
  flex-shrink: 0;
  opacity: 0.6;

  &.is-working {
    opacity: 1;
  }
}

.process-trace__title {
  flex: 1;
  min-width: 0;
  font-size: 13px;
  color: #44506a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.process-trace__count {
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 500;
  color: #5b6f8f;
  font-variant-numeric: tabular-nums;
}

.process-trace__chevron {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 12px;
  height: 12px;
  color: #6d7ea0;

  i {
    font-size: 10px;
    transform: rotate(90deg);
    transition: transform 0.15s ease;
  }

  &.is-expanded i {
    transform: rotate(270deg);
  }
}

.process-trace__more {
  border: 0;
  background: transparent;
  padding: 0;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  color: #6d7ea0;
  cursor: pointer;
  font-size: 12px;

  &:hover {
    background: rgba(74, 95, 243, 0.12);
    color: #4a5ff3;
  }
}

/* ===== 展开列表（浅蓝底、限高滚动） ===== */
.process-trace__body {
  max-height: 232px;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 2px 6px 6px;
  background: #f0f5ff;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(74, 95, 243, 0.25);
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(74, 95, 243, 0.45);
  }
  scrollbar-width: thin;
  scrollbar-color: rgba(74, 95, 243, 0.25) transparent;
}

/* ===== 单行（沿用原有密度：12px 字号 + 4/6 padding） ===== */
.trace-row {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 4px 6px;
  border: 1px solid transparent;
  border-radius: 6px;
  font-size: 12px;
  line-height: 1.5;
  color: #4b5563;
  cursor: pointer;
  animation: trace-row-in 0.18s ease-out both;

  /* 可点开详情的内容：悬停下划线，不做整行背景变化 */
  &:hover .trace-row__compound,
  &:hover .trace-row__text {
    text-decoration: underline;
    text-underline-offset: 2px;
  }

  &:hover .trace-row__arrow {
    opacity: 1;
  }
}

@keyframes trace-row-in {
  from {
    opacity: 0;
    transform: translateX(-8px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

/* 行首图标：沿用原方案（emoji / base64 图片 / 虚线圆 SVG） */
.trace-row__icon {
  flex-shrink: 0;
  font-size: 11px;
  width: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.trace-icon-img {
  width: 12px;
  height: 12px;
  flex-shrink: 0;
  object-fit: contain;
}

/* 品牌图标（客户端 customIcon 同样带 rounded-sm） */
.trace-icon-img--brand {
  border-radius: 2px;
}

.trace-icon-svg {
  width: 12px;
  height: 12px;
  flex-shrink: 0;
  color: #6b7280;
}

/* 中间叙述仍在进行：虚线圆旋转，对齐目标端的 Spinner 状态 */
.trace-icon-svg.is-spinning {
  animation: trace-spin 1.2s linear infinite;
}

@keyframes trace-spin {
  to {
    transform: rotate(360deg);
  }
}

/* 待开始的工具行：实心圆，弱化（对齐目标端 text-muted-foreground/50） */
.trace-icon-svg--pending {
  color: #9ca3af;
  opacity: 0.6;
}

/* diff 徽标：删除红 / 新增绿 */
.trace-row__diff {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  font-size: 10px;
}

.trace-row__del,
.trace-row__add {
  padding: 0 6px;
  border-radius: 4px;
  line-height: 16px;
}

.trace-row__del {
  background: rgba(220, 38, 38, 0.06);
  color: #dc2626;
}

.trace-row__add {
  background: rgba(16, 185, 129, 0.06);
  color: #059669;
}

/* 文件名徽标（沿用原 badge 风格） */
.trace-row__file {
  flex-shrink: 0;
  padding: 0 4px;
  border-radius: 4px;
  background: rgba(15, 23, 42, 0.06);
  font-size: 10px;
  line-height: 14px;
  color: #64748b;
}

/* 「· 意图 · 参数摘要」这一段负责占满剩余宽度并截断 */
.trace-row__compound {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--mc-text-tertiary);
}

.trace-row__sep {
  color: rgba(35, 34, 39, 0.3);
}

/* 品牌名前缀（Python: / Git: …），对齐目标端 formatToolDisplay */
.trace-row__brand {
  margin-right: 4px;
}

.trace-row__params {
  opacity: 0.5;
}

/* 全屏查看箭头：默认隐藏，鼠标移入该行才出现（对齐目标 group-hover/row） */
.trace-row__arrow {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  border-radius: 3px;
  color: #b6c2d1;
  opacity: 0;
  transition: opacity 0.15s ease, color 0.15s ease, background 0.15s ease;

  i {
    font-size: 10px;
  }

  &:hover {
    background: rgba(15, 23, 42, 0.06);
  }
}

/* ===== 叙述行 ===== */
.trace-row__text {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--mc-text-tertiary);
}

/* 展开/收起箭头：常显，展开后旋转 90°（对齐目标 ChevronRight） */
.trace-row__note-chevron {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 12px;
  height: 12px;
  color: rgba(107, 114, 128, 0.6);

  i {
    font-size: 10px;
    transform: rotate(0deg);
    transition: transform 0.15s ease;
  }

  &.is-open i {
    transform: rotate(90deg);
  }
}

/* 打字期间整段铺开（不截断），流完自动收回单行 */
.trace-row--pending {
  align-items: flex-start;

  .trace-row__icon {
    margin-top: 2px;
  }

  .trace-row__text {
    white-space: normal;
    overflow: visible;
    text-overflow: clip;
    word-break: break-word;
    line-height: 20px;
    max-height: 120px;
    overflow-y: auto;
  }

  .trace-row__caret {
    margin-top: 7px;
  }
}

/* 打字机光标：弱化灰条，保留"正在打字"暗示但不抢视线 */
.trace-row__caret {
  flex-shrink: 0;
  width: 1.5px;
  height: 12px;
  border-radius: 1px;
  background: #c0c4cc;
  animation: trace-caret 1s steps(1, end) infinite;
}

@keyframes trace-caret {
  0%,
  50% {
    opacity: 1;
  }
  50.01%,
  100% {
    opacity: 0;
  }
}

/* 叙述行行内展开的全文：等宽小字，缩进对齐行内容 */
.trace-note-detail {
  padding: 0 8px 6px 20px;
  font-family: 'Cascadia Code', 'Fira Code', 'JetBrains Mono', Consolas, monospace;
  font-size: 11px;
  line-height: 1.65;
  color: #6b7280;
  white-space: pre-wrap;
  word-break: break-word;
}

/* ===== TodoWrite 列表 ===== */
.process-trace__todos {
  padding-left: 20px;
  margin-top: 4px;
}

.todo-row {
  display: flex;
  align-items: center;
  gap: 6px;
  height: 24px;
  font-size: 13px;
  color: #4b5563;

  &.is-completed .todo-row__text {
    color: #9aa4b2;
    text-decoration: line-through;
  }
}

.todo-row__mark {
  flex-shrink: 0;
  width: 12px;
  text-align: center;
  font-size: 10px;
  color: #9aa4b2;

  .is-completed & {
    color: #059669;
  }

  .is-in_progress & {
    color: #4a5ff3;
  }
}

.todo-row__text {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ===== 折叠动画 ===== */
.trace-slide-enter-active,
.trace-slide-leave-active {
  transition: opacity 0.15s ease, transform 0.18s ease;
}

.trace-slide-enter,
.trace-slide-leave-to {
  opacity: 0;
  transform: translateY(-3px);
}
</style>
